import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '@daycal/ui';
import { 
  FiCalendar, FiMail, FiMapPin, FiClock, 
  FiShield, FiPhone, FiUser, FiRotateCcw, FiFileText,
  FiExternalLink, FiChevronDown
} from 'react-icons/fi';
import { useLocale } from '../contexts/LocaleContext.jsx';
import { useLegalCopy } from '../lib/legalContent.js';

function Logo() {
  return (
    <Link to="/" className="group flex items-center">
      <img src="/Day cal logo name.png" alt="DayCal Logo" className="h-16 w-auto -ml-3 -my-3 object-contain transition-transform duration-500 group-hover:scale-105" />
    </Link>
  );
}

function LangToggle({ locale, setLocale }) {
  return (
    <div className="flex items-center rounded-full bg-slate-100 p-0.5 text-xs font-semibold">
      {[['en', 'ENG'], ['bn', 'BN']].map(([code, label]) => (
        <button
          key={code}
          type="button"
          onClick={() => setLocale(code)}
          className={`rounded-full px-2.5 py-1 transition-colors ${
            locale === code ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export default function Contact() {
  const { locale, setLocale } = useLocale();
  const t = useLegalCopy('contact', locale);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col justify-between">
      <SEO path="/contact" title={`${t.title} - DayCal`} description={t.subtitle} />
      
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'pt-4 px-4' : 'pt-0'}`}>
        <div className={`mx-auto flex items-center justify-between transition-all duration-300 ${
          isScrolled 
            ? 'max-w-4xl rounded-full bg-white/95 backdrop-blur-lg shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-slate-200/50 px-6 py-2.5' 
            : 'max-w-6xl bg-white/90 backdrop-blur-md px-5 py-3.5 border-b border-slate-200/80'
        }`}>
          <Logo />
          <nav className="hidden items-center gap-6 text-sm font-medium text-slate-600 md:flex">
            <Link to="/" className="hover:text-slate-900 transition-colors">Home</Link>
            <Link to="/privacy" className="hover:text-slate-900 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-slate-900 transition-colors">Terms of Service</Link>
            <Link to="/refund" className="hover:text-slate-900 transition-colors">Refund Policy</Link>
            <Link to="/contact" className="text-brand-600 font-semibold">Contact Us</Link>
          </nav>
          <div className="flex items-center gap-3">
            <LangToggle locale={locale} setLocale={setLocale} />
            <Link to="/login" className="rounded-dc-sm bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-slate-800">
              Sign in
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white to-slate-100/70 border-b border-slate-200/60 py-16">
        <div className="pointer-events-none absolute inset-0 bg-dot-grid opacity-60" />
        <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-0 h-72 w-72 rounded-full bg-brand-200/40 blur-3xl" />
        <div className="relative mx-auto max-w-5xl px-5 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-brand-300 bg-brand-50 px-3.5 py-1 text-xs font-semibold text-brand-700 shadow-sm">
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" /> {t.badge}
          </span>
          <h1 className="font-display mt-5 text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
            {t.title}
          </h1>
          <p className="mt-4 max-w-2xl mx-auto text-base sm:text-lg text-slate-600 leading-relaxed">
            {t.subtitle}
          </p>
        </div>
      </section>

      {/* Main Content Hub */}
      <main className="mx-auto max-w-6xl px-5 py-14 flex-1 w-full">
        <div className="grid gap-8 lg:grid-cols-12">
          
          {/* Primary Contact Channels - 7 Cols */}
          <div className="lg:col-span-7 space-y-6">
            
            <div className="rounded-dc border border-slate-200 bg-white p-8 shadow-sm">
              <h3 className="font-display text-xl font-bold text-slate-900 mb-6 flex items-center gap-2.5">
                <FiUser className="h-5 w-5 text-brand-600" /> {t.directContactTitle}
              </h3>
              
              <div className="space-y-6 text-sm">
                
                {/* Officer Name */}
                <div className="flex items-start gap-4 p-4 rounded-dc-sm bg-slate-50 border border-slate-100">
                  <div className="p-2.5 rounded-dc-sm bg-brand-100/80 text-brand-700 shrink-0">
                    <FiUser className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-sm">{t.officerTitle}</h4>
                    <p className="text-slate-800 text-base font-semibold mt-0.5">{t.officerName}</p>
                    <p className="text-slate-500 text-xs mt-0.5">{t.officerSubtext}</p>
                  </div>
                </div>

                {/* Email Support */}
                <div className="flex items-start gap-4 p-4 rounded-dc-sm bg-slate-50 border border-slate-100">
                  <div className="p-2.5 rounded-dc-sm bg-emerald-100/80 text-emerald-700 shrink-0">
                    <FiMail className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-slate-900 text-sm">{t.emailTitle}</h4>
                    <a href={`mailto:${t.emailValue}`} className="text-brand-600 hover:underline text-base font-semibold mt-0.5 block">
                      {t.emailValue}
                    </a>
                    <p className="text-slate-500 text-xs mt-0.5">{t.slaValue}</p>
                  </div>
                </div>

                {/* Direct Phone */}
                <div className="flex items-start gap-4 p-4 rounded-dc-sm bg-slate-50 border border-slate-100">
                  <div className="p-2.5 rounded-dc-sm bg-purple-100/80 text-purple-700 shrink-0">
                    <FiPhone className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-slate-900 text-sm">{t.phoneTitle}</h4>
                    <a href={`tel:${t.phoneValue.replace(/\s+/g, '')}`} className="text-brand-600 hover:underline text-base font-semibold mt-0.5 block">
                      {t.phoneValue}
                    </a>
                    <p className="text-slate-500 text-xs mt-0.5">{t.hoursValue}</p>
                  </div>
                </div>

                {/* Official Operating Address */}
                <div className="flex items-start gap-4 p-4 rounded-dc-sm bg-slate-50 border border-slate-100">
                  <div className="p-2.5 rounded-dc-sm bg-sky-100/80 text-sky-700 shrink-0">
                    <FiMapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-sm">{t.addressTitle}</h4>
                    <p className="text-slate-700 text-sm mt-1 leading-relaxed font-medium">
                      {t.addressValue}
                    </p>
                  </div>
                </div>

              </div>
            </div>

          </div>

          {/* Verification & Compliance Resources - 5 Cols */}
          <div className="lg:col-span-5 space-y-6">
            
            {/* Billing & Refund Policy Callout */}
            <div className="rounded-dc border border-brand-200 bg-gradient-to-br from-brand-50/50 via-white to-white p-7 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-dc-sm bg-brand-600 text-white">
                  <FiRotateCcw className="h-5 w-5" />
                </div>
                <h4 className="font-bold text-slate-900 text-base">{t.billingTitle}</h4>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed mb-4">
                {t.billingDesc}
              </p>
              <Link 
                to="/refund" 
                className="inline-flex items-center gap-2 rounded-dc-sm bg-slate-900 px-4 py-2.5 text-xs font-semibold text-white hover:bg-slate-800 transition-colors shadow-sm"
              >
                <FiRotateCcw className="h-3.5 w-3.5 text-emerald-400" />
                <span>{t.refundBtn}</span>
                <FiExternalLink className="h-3.5 w-3.5 opacity-70" />
              </Link>
            </div>

            {/* Legal Documents Navigation */}
            <div className="rounded-dc border border-slate-200 bg-white p-6 shadow-sm">
              <h4 className="font-display text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
                <FiFileText className="h-4 w-4 text-brand-600" /> {t.quickLinksTitle}
              </h4>
              <div className="space-y-2 text-xs">
                <Link to="/privacy" className="flex items-center justify-between p-2.5 rounded-dc-sm hover:bg-slate-50 text-slate-700 font-medium transition-colors border border-transparent hover:border-slate-100">
                  <span>{t.linkPrivacy}</span>
                  <span className="text-brand-600 font-bold">&rarr;</span>
                </Link>
                <Link to="/terms" className="flex items-center justify-between p-2.5 rounded-dc-sm hover:bg-slate-50 text-slate-700 font-medium transition-colors border border-transparent hover:border-slate-100">
                  <span>{t.linkTerms}</span>
                  <span className="text-brand-600 font-bold">&rarr;</span>
                </Link>
                <Link to="/refund" className="flex items-center justify-between p-2.5 rounded-dc-sm hover:bg-slate-50 text-slate-700 font-medium transition-colors border border-transparent hover:border-slate-100">
                  <span>{t.linkRefund}</span>
                  <span className="text-brand-600 font-bold">&rarr;</span>
                </Link>
              </div>
            </div>

          </div>

          {/* FAQ Section - 12 Cols */}
          <div className="lg:col-span-12 mt-4 space-y-6">
            <div className="rounded-dc border border-slate-200 bg-white p-8 shadow-sm">
              <h3 className="font-display text-2xl font-bold text-slate-900 mb-8 text-center">
                {t.faqTitle}
              </h3>
              <div className="mx-auto max-w-3xl space-y-4">
                {t.faqs.map((faq, index) => (
                  <details key={index} className="group rounded-dc-sm border border-slate-200 bg-slate-50 [&_summary::-webkit-details-marker]:hidden">
                    <summary className="flex cursor-pointer items-center justify-between gap-1.5 p-4 text-slate-900 font-semibold focus:outline-none focus-visible:ring focus-visible:ring-brand-500/50 rounded-dc-sm">
                      <span className="text-base">{faq.q}</span>
                      <FiChevronDown className="h-5 w-5 shrink-0 transition duration-300 group-open:-rotate-180 text-slate-500" />
                    </summary>
                    <div className="px-4 pb-4 text-slate-600 text-sm leading-relaxed border-t border-slate-200 pt-4 mt-1 bg-white rounded-b-dc-sm">
                      {faq.a}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </div>

        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white py-8">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-5 text-xs text-slate-500 sm:flex-row">
          <Logo />
          <p>© {new Date().getFullYear()} DayCal. All rights reserved.</p>
          <div className="flex flex-wrap gap-6 justify-center">
            <Link to="/contact" className="text-brand-600 font-bold">Contact Us</Link>
            <Link to="/privacy" className="hover:text-slate-900 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-slate-900 transition-colors">Terms of Service</Link>
            <Link to="/refund" className="hover:text-slate-900 transition-colors">Refund Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
